// Apiservices/Auth/servers/venueService.js
import axios from "../../../util/axiosInstance";

import config from '../../../config';

const API_BASE_URL = config.API_BASE_URL;

// Search venues
export const searchVenues = async (params) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/venues/search`, { params });
    return {
      success: response.data.success,
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error searching venues:', error);
    return { success: false, data: [], error: error.message };
  }
};

// Fetch venue detail
export const fetchVenueDetail = async (venueId, latitude, longitude) => {
  try {
    const params = {};
    if (latitude) params.latitude = latitude;
    if (longitude) params.longitude = longitude;
    
    const response = await axios.get(`${API_BASE_URL}/v1/venues/${venueId}/detail`, { params });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error('Failed to fetch venue details');
  } catch (error) {
    console.error('Error fetching venue details:', error);
    throw error;
  }
};

// Get pricing estimate
export const getEstimate = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/v1/venues/estimate`, data);
    return {
      success: response.data.success,
      estimate: response.data.estimate,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error getting estimate:', error);
    return { success: false, error: error.message };
  }
};

// Add venue to cart
export const addVenueToCart = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/v1/cart/venue`, data);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error adding to cart:', error);
    return { success: false, error: error.message };
  }
};

// Fetch availability slots
export const fetchAvailability = async (venueId, spaceId, month) => {
  try {
    const params = { venueId, spaceId };
    if (month) params.month = month;
    
    const response = await axios.get(`${API_BASE_URL}/v1/venues/availability`, { params });
    if (response.data.success) {
      return response.data.data?.blockedSlots || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching availability:', error);
    return [];
  }
};

// Fetch terms status
export const fetchTermsStatus = async (venueId, eventId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/venues/terms-status`, {
      params: { venueId, myEventId: eventId }
    });
    if (response.data.success) {
      return response.data.data || {};
    }
    return {};
  } catch (error) {
    console.error('Error fetching terms status:', error);
    return {};
  }
};

// Accept venue/space terms
export const acceptTerms = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/v1/venues/accept-terms`, data);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error accepting terms:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || 'Failed to accept terms'
    };
  }
};

// Check availability for specific slot
export const checkAvailability = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/v1/venues/check-availability`, data);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error checking availability:', error);
    return { success: false, error: error.message };
  }
};

// Get venue rules (Public)
export const fetchVenueRules = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/rules`);
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching venue rules:', error);
    return [];
  }
};

// Get space categories (Public)
export const fetchSpaceCategories = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/space-categories`);
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching space categories:', error);
    return [];
  }
};

// Get space types by category (Public)
export const fetchSpaceTypesByCategory = async (categoryId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/space-types`, {
      params: { categoryId }
    });
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching space types:', error);
    return [];
  }
};

// Get all space types with categories (Public)
export const fetchAllSpaceTypes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/space-types/all`);
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching all space types:', error);
    return [];
  }
};

// Get space features by type (Public)
export const fetchSpaceFeatures = async (typeId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/space-features`, {
      params: { typeId }
    });
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching space features:', error);
    return [];
  }
};

// Edit cart item
export const editCartItem = async (cartItemId, data) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/v1/cart/venue/${cartItemId}`, data);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error editing cart item:', error);
    return { success: false, error: error.message };
  }
};

// Remove from cart
export const removeFromCart = async (cartItemId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/v1/cart/venue/${cartItemId}`);
    return {
      success: response.data.success,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error removing from cart:', error);
    return { success: false, error: error.message };
  }
};

// Get cart items
export const getCartItems = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/cart/venue`);
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching cart items:', error);
    return [];
  }
};

// Checkout (Create payment order)
export const checkoutVenue = async (cartItemId, amount, couponCode = null) => {
  try {
    const payload = { amount };
    if (couponCode && couponCode.trim()) {
      payload.couponCode = couponCode.trim();
    }
    const response = await axios.post(`${API_BASE_URL}/v1/cart/venue/${cartItemId}/checkout`, payload);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error during checkout:', error);
    return { success: false, error: error.message };
  }
};

// Verify payment
export const verifyPayment = async (cartItemId, paymentData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/v1/cart/venue/${cartItemId}/payments/verify`,
      paymentData
    );
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error verifying payment:', error);
    return { success: false, error: error.message };
  }
};

// Get orders
export const getVenueOrders = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/v1/orders/venue`);
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    return [];
  }
};

// Cancel order
export const cancelOrder = async (cartItemId, reason = null) => {
  try {
    const payload = {};
    if (reason) payload.reason = reason;
    const response = await axios.post(`${API_BASE_URL}/v1/cart/venue/${cartItemId}/cancel`, payload);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error canceling order:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// EXPORT ALL FUNCTIONS
// ============================================

export default {
  searchVenues,
  fetchVenueDetail,
  getEstimate,
  addVenueToCart,
  fetchAvailability,
  fetchTermsStatus,
  acceptTerms,
  checkAvailability,
  fetchVenueRules,
  fetchSpaceCategories,
  fetchSpaceTypesByCategory,
  fetchAllSpaceTypes,
  fetchSpaceFeatures,
  editCartItem,
  removeFromCart,
  getCartItems,
  checkoutVenue,
  verifyPayment,
  getVenueOrders,
  cancelOrder,
};